import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import type { ReactNode } from "react";
import type { RingState } from "../../shared/api-contract";
import { useRing } from "./ringContext";
import { useCaps } from "../editor/CapsProvider";
import { Button, FileButton, Icon, SectionLabel, Toast, useToast } from "../kit";
import { downloadBlob, downloadJSON } from "../../util/download";
import { useShareDesign } from "./useShareDesign";
import { ringLabel, useRingSet } from "./RingManagerModal";
import { SpecSheet, type SpecSheetRing } from "./SpecSheet";
import { EASE_OUT } from "../theme";
import { useViewer } from "../../state/ViewerProvider";
import { SendToGoldsmithDialog } from "../../features/goldsmith/SendToGoldsmithDialog";
import { CncExportDialog } from "./cnc/CncExportDialog";

/**
 * The Ring Builder footer action row: four triggers (Spec Sheet · Download ·
 * Save / Load · Manufacturing) that open a popup panel above the footer.
 *
 * It merges three older surfaces:
 *  - engagement `ActionBar` — Save Design, Share Link, Download GLB/STL, toast;
 *  - band `ExportStep`     — Export GLB, Snapshot PNG, Export/Import settings
 *                            JSON, and the read-only configuration dump (now
 *                            the Spec Sheet panel);
 *  - the reference footer  — the four-action grouping itself.
 *
 * Manufacturing (CSG-fused GLB/STL) is offered for every ring, band included
 * (v43) — it fuses whatever metal the ring has.
 *
 * "Spec Sheet" opens the printable document (`SpecSheet.tsx`) and keeps the raw
 * JSON dump as a second action.
 */

type MenuId = "spec" | "download" | "save" | "mfg";
type Busy = "save" | "share" | "glb" | "stl" | "model" | "ring" | "png" | "settings" | null;

/** Coerce the manufacturing-export RPC's `Blob | ArrayBuffer | string`
 * return into a downloadable Blob. */
function toDownloadBlob(result: Blob | ArrayBuffer | string, mime: string): Blob {
  if (result instanceof Blob) return result;
  if (result instanceof ArrayBuffer) return new Blob([result], { type: mime });
  const trimmed = result.trim();
  const looksBase64 = trimmed.length > 0 && !trimmed.startsWith("{") && /^[A-Za-z0-9+/=]+$/.test(trimmed);
  if (looksBase64) {
    try {
      const bin = atob(trimmed);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    } catch {
      /* not actually base64 — fall through to plain text */
    }
  }
  return new Blob([result], { type: mime });
}

export interface ActionBarProps {
  /** Which ring the builder is editing (`setActiveRing`). */
  activeRing?: number;
  /** Rings in the set, ≥ 1. Drives the spec sheet's per-ring sections. */
  ringCount?: number;
}

export function ActionBar({ activeRing = 0, ringCount = 1 }: ActionBarProps = {}) {
  const { state, catalogs, r, refresh, engagementOn } = useRing();
  const set = useRingSet();
  const caps = useCaps();
  const { message, show } = useToast(2400);
  const [busy, setBusy] = useState<Busy>(null);
  const [menu, setMenu] = useState<MenuId | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [goldsmithOpen, setGoldsmithOpen] = useState(false);
  const [cncOpen, setCncOpen] = useState(false);
  const { client } = useViewer();
  const rootRef = useRef<HTMLDivElement>(null);
  // One share implementation for the two entries below and the floating Share
  // button over the viewer — see `useShareDesign`.
  const { share } = useShareDesign();

  // Close the popup on an outside click (the file picker lives inside the root,
  // so Import Settings is unaffected).
  useEffect(() => {
    if (!menu) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMenu(null);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menu]);

  const fileBase = engagementOn ? "engagement-ring" : "wedding-band";

  // The viewer only ever hands back the ACTIVE ring (`getRingState` has no index
  // argument), so the other ring's figures are the ones it reported when it was
  // last selected — geometry cannot change while a ring is deselected. Same
  // cache SpecPanel keeps for its price rows.
  const rings = useRef<Record<number, RingState>>({}).current;
  rings[activeRing] = state;
  for (const key of Object.keys(rings)) if (Number(key) >= Math.max(1, ringCount)) delete rings[Number(key)];
  const sheetRings: SpecSheetRing[] = Array.from({ length: Math.max(1, ringCount) }, (_, i) => i)
    .filter((i) => rings[i])
    // Her/His only means anything in pair mode; a lone ring is just "the ring".
    // Names come from the manager's rows, so a renamed ring prints its name.
    .map((i) => ({ label: ringCount > 1 ? ringLabel(set.rows, i) : "Ring", state: rings[i] }));

  /** The band Export step's read-only summary, plus the engagement block. */
  const summary = {
    profile: state.profileId,
    ringSize: `US ${state.ringSizeUS} / EU ${state.ringSizeEU}`,
    width: state.widthMM + "mm",
    thickness: state.thicknessMM + "mm",
    edgeStyle: state.edgeStyle,
    grooves: state.grooves,
    splitMode: state.splitMode,
    splitPattern: state.splitPatternId,
    materials: state.materials,
    diamond: state.diamond,
    engraving: state.engravingText || "(none)",
    engravingOptions: state.engraving,
    ...(engagementOn ? { engagement: state.engagement } : {}),
  };

  const downloadSpecJSON = () => downloadJSON(JSON.stringify(summary, null, 2), `${fileBase}-spec.json`);

  const run = async (tag: Exclude<Busy, null>, label: string, fn: () => Promise<void>) => {
    setBusy(tag);
    try {
      await fn();
    } catch (err) {
      show(`${label} failed: ${(err as Error).message || "unknown error"}`);
    } finally {
      setBusy(null);
    }
  };

  const onSaveDesign = () =>
    run("save", "Save", async () => {
      const result = await share();
      show(result.mode === "vault" ? "Saved to Vault — link copied" : "Saved locally — link copied");
    });

  const onShareLink = () =>
    run("share", "Share", async () => {
      await share();
      show("Link copied");
    });

  const onManufacturing = (format: "glb" | "stl") =>
    run(format, "Export", async () => {
      const result = await r.exportManufacturingGLB({ format });
      const mime = format === "stl" ? "model/stl" : "model/gltf-binary";
      downloadBlob(toDownloadBlob(result, mime), `${fileBase}.${format}`);
    });

  /**
   * The ACTIVE ring on its own (the reference's `Download` beside its `Download
   * both rings`). `exportGLB` walks the scene, and `GlbExportOptions` has no ring
   * selector, so the ring is isolated by hiding the others and exporting
   * `onlyVisible` — which is what `setRingVisible` (v43) is for. Visibility is
   * restored in `finally`, including for a ring the manager had already hidden.
   */
  const onDownloadRing = () =>
    run("ring", "Export", async () => {
      const hide = set.rows.map((_, i) => i).filter((i) => i !== activeRing && set.rows[i].visible);
      try {
        for (const i of hide) await r.setRingVisible(i, false);
        const name = ringLabel(set.rows, activeRing).toLowerCase().replace(/[^a-z0-9]+/g, "-");
        downloadBlob(await r.exportGLB({ onlyVisible: true }), `${name}.glb`);
      } finally {
        for (const i of hide) await r.setRingVisible(i, true);
      }
    });

  // A band only reaches the real fuser on v43; before that the bridge fell back
  // to a plain scene GLB and wrote it under whatever extension was asked for.
  const exportReady = engagementOn ? caps.engagementSpecV35 : caps.ringManagerV43;
  const exportWhy = engagementOn ? "Requires viewer contract v35+" : "Requires viewer contract v43+";
  const saveReady = caps.ringBuilderV37;
  const disabled = busy !== null;

  return (
    <div
      ref={rootRef}
      className="relative flex shrink-0 flex-col gap-2 border-t border-outline-variant/40 bg-surface-container-high/30 px-6 py-3"
    >
      <AnimatePresence>
        {menu ? (
          <motion.div
            key={menu}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.16, ease: EASE_OUT }}
            className="scroll-thin absolute bottom-[calc(100%-4px)] left-4 right-4 z-40 max-h-[60vh] overflow-auto rounded-xl border border-outline-variant/40 bg-surface-container-lowest p-4 shadow-[0_12px_40px_rgba(0,0,0,0.10)]"
          >
            {menu === "spec" ? (
              <div className="flex flex-col gap-2">
                <SectionLabel>Specification</SectionLabel>
                <Button variant="primary" full onClick={() => setSheetOpen(true)}>
                  <Icon name="description" size={15} />
                  Open Spec Sheet (Print / PDF)
                </Button>

                <SectionLabel>Current Configuration</SectionLabel>
                <pre
                  data-testid="ring-spec-sheet"
                  className="scroll-thin max-h-72 overflow-auto rounded-lg border border-outline-variant/30 bg-surface-container-low p-3 font-mono text-[10px] leading-relaxed text-on-surface-variant"
                >
{JSON.stringify(summary, null, 2)}
                </pre>
                <Button variant="outline" full onClick={downloadSpecJSON}>
                  Download Spec Sheet (.json)
                </Button>
              </div>
            ) : null}

            {menu === "download" ? (
              <div className="flex flex-col gap-2">
                <SectionLabel>Model & Image</SectionLabel>
                {/*
                  v42 — the GLB has always contained the whole ring set (the
                  export walks the scene, not the active ring), which nothing on
                  screen said. The item now names what it writes, and counts:
                  "both rings" for a pair, "all 4 rings" for four.
                */}
                <Button
                  variant="outline"
                  full
                  disabled={disabled}
                  onClick={() =>
                    void run("model", "Export", async () => {
                      downloadBlob(await r.exportGLB(), `${fileBase}${ringCount > 1 ? "-set" : ""}.glb`);
                    })
                  }
                >
                  {busy === "model"
                    ? "Exporting…"
                    : ringCount === 2
                      ? "Download Both Rings (One GLB)"
                      : ringCount > 2
                        ? `Download All ${ringCount} Rings (One GLB)`
                        : "Export GLB (3D Model)"}
                </Button>
                {ringCount > 1 ? (
                  <span className="-mt-1 font-body text-[10px] text-outline">
                    One file, {ringCount} rings, posed as they are on screen.
                  </span>
                ) : null}
                {ringCount > 1 && caps.ringManagerV43 ? (
                  <Button variant="outline" full disabled={disabled} onClick={() => void onDownloadRing()}>
                    {busy === "ring" ? "Exporting…" : `Download ${ringLabel(set.rows, activeRing)} Only`}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  full
                  disabled={disabled}
                  onClick={() =>
                    void run("png", "Snapshot", async () => {
                      downloadBlob(await r.snapshot({ width: 2048, height: 2048 }), `${fileBase}.png`);
                    })
                  }
                >
                  {busy === "png" ? "Rendering…" : "Snapshot PNG (Image)"}
                </Button>
              </div>
            ) : null}

            {menu === "save" ? (
              <div className="flex flex-col gap-2">
                <SectionLabel>Design</SectionLabel>
                <Gated ready={saveReady} why="Requires viewer contract v37+">
                  <Button variant="primary" full disabled={disabled || !saveReady} onClick={() => void onSaveDesign()}>
                    <Icon name="save" size={15} />
                    {busy === "save" ? "Saving…" : "Save Design"}
                  </Button>
                </Gated>
                <Gated ready={saveReady} why="Requires viewer contract v37+">
                  <Button variant="outline" full disabled={disabled || !saveReady} onClick={() => void onShareLink()}>
                    <Icon name="link" size={15} />
                    {busy === "share" ? "Sharing…" : "Share Link"}
                  </Button>
                </Gated>

                <SectionLabel>Scene Settings</SectionLabel>
                <Button
                  variant="outline"
                  full
                  disabled={disabled}
                  onClick={() =>
                    void run("settings", "Export settings", async () => {
                      downloadJSON(await r.exportSettingsJSON(), "settings.json");
                    })
                  }
                >
                  Export Settings (settings.json)
                </Button>
                <FileButton
                  accept="application/json,.json"
                  full
                  onFile={(file) =>
                    void run("settings", "Import settings", async () => {
                      await r.importSettingsJSON(await file.text());
                      await refresh();
                      show("Settings imported");
                    })
                  }
                >
                  Import Settings (settings.json)
                </FileButton>
              </div>
            ) : null}

            {menu === "mfg" ? (
              <div className="flex flex-col gap-2">
                <SectionLabel>Manufacturing Export</SectionLabel>
                <p className="font-body text-[11px] leading-relaxed text-on-surface-variant">
                  Watertight CSG-fused geometry of {ringCount > 1 ? "the ring being edited" : "the ring"}, for
                  casting — band, settings and {engagementOn ? "head" : "beads"} unioned into one solid. Stones
                  ride along in the GLB as separate meshes; the STL is metal only.
                </p>
                <Gated ready={exportReady} why={exportWhy}>
                  <Button
                    variant="outline"
                    full
                    disabled={disabled || !exportReady}
                    onClick={() => void onManufacturing("glb")}
                  >
                    <Icon name="download" size={15} />
                    {busy === "glb" ? "Exporting…" : "Download GLB"}
                  </Button>
                </Gated>
                <Gated ready={exportReady} why={exportWhy}>
                  <Button
                    variant="outline"
                    full
                    disabled={disabled || !exportReady}
                    onClick={() => void onManufacturing("stl")}
                  >
                    <Icon name="download" size={15} />
                    {busy === "stl" ? "Exporting…" : "Download STL"}
                  </Button>
                </Gated>

                {/* v66 — the band as a 4-axis rotary program for the shop's
                    machine. Three brand-new method names behind it, so the
                    entry stays disabled on an older viewer (RPC has no timeout). */}
                <SectionLabel>CNC Machining</SectionLabel>
                <Gated ready={caps.ringCncV66} why="Requires viewer contract v66+">
                  <Button
                    variant="outline"
                    full
                    disabled={disabled || !caps.ringCncV66}
                    onClick={() => { setMenu(null); setCncOpen(true); }}
                  >
                    <Icon name="precision_manufacturing" size={15} />
                    CNC program (.NC)
                  </Button>
                </Gated>
              </div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* v43: Manufacturing is offered unconditionally, as the reference offers it
          on a plain wedding-band demo. It used to be hidden without an engagement
          head — and was a no-op even with one, because the band route has no
          EngagementConfigurator; the band now fuses through `exportManufacturingRing`. */}
      <div className="grid grid-cols-4 gap-1.5">
        <ActionTrigger id="spec" icon="description" label="Spec Sheet" menu={menu} onSelect={setMenu} />
        <ActionTrigger id="download" icon="download" label="Download" menu={menu} onSelect={setMenu} />
        <ActionTrigger id="save" icon="save" label="Save / Load" menu={menu} onSelect={setMenu} />
        <ActionTrigger id="mfg" icon="precision_manufacturing" label="Manufacturing" menu={menu} onSelect={setMenu} />
      </div>
      {/* The finished ring, onward to TheGoldsmith for an AI photoshoot or video. */}
      <button
        type="button"
        data-testid="ring-action-goldsmith"
        onClick={() => { setMenu(null); setGoldsmithOpen(true); }}
        className="mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border border-outline-variant/50 bg-surface px-2 py-2 font-display text-[10px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant transition-colors hover:border-primary hover:text-primary"
      >
        <Icon name="auto_awesome" size={15} />
        Send to TheGoldsmith · AI photo & video
      </button>
      {cncOpen && caps.ringCncV66 ? <CncExportDialog onClose={() => setCncOpen(false)} /> : null}
      <SendToGoldsmithDialog open={goldsmithOpen} onClose={() => setGoldsmithOpen(false)} client={client} category="rings" />

      {sheetOpen ? (
        <SpecSheet
          rings={sheetRings}
          catalogs={catalogs}
          onClose={() => setSheetOpen(false)}
          onDownloadJSON={downloadSpecJSON}
        />
      ) : null}

      {/* Harness hook kept verbatim from the engagement ActionBar. */}
      <Toast message={message} testId="engagement-action-toast" />
    </div>
  );
}

/** Footer trigger: icon over a small caps label, underlined while its panel is open. */
function ActionTrigger({
  id,
  icon,
  label,
  menu,
  onSelect,
}: {
  id: MenuId;
  icon: string;
  label: string;
  menu: MenuId | null;
  onSelect: (id: MenuId | null) => void;
}) {
  const open = menu === id;
  return (
    <button
      type="button"
      data-testid={`ring-action-${id}`}
      aria-expanded={open}
      onClick={() => onSelect(open ? null : id)}
      className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 font-display text-[9px] font-semibold uppercase tracking-[0.1em] transition-colors ${
        open
          ? "border-primary bg-surface text-primary"
          : "border-outline-variant/50 bg-surface text-on-surface-variant hover:border-primary hover:text-primary"
      }`}
    >
      <Icon name={icon} size={17} />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

/** Wraps a caps-gated button so the disabled control still explains itself. */
function Gated({ ready, why, children }: { ready: boolean; why: string; children: ReactNode }) {
  return (
    <span className="block" title={ready ? undefined : why}>
      {children}
    </span>
  );
}
